/**
 * サウンドマネージャー
 * Phaser Audioを使用した効果音・BGM再生機能
 * settingsStoreのオーディオ設定と連携
 */
import Phaser from 'phaser';
import { useSettingsStore } from '@/stores/settingsStore';

// サウンドキーの定義
export const SoundKeys = {
  // 効果音
  CARD_PLAY: 'sfx_card_play',
  CARD_DRAW: 'sfx_card_draw',
  CARD_DEAL: 'sfx_card_deal',
  SKIP: 'sfx_skip',
  REVERSE: 'sfx_reverse',
  UNO_CALL: 'sfx_uno_call',
  ROUND_WIN: 'sfx_round_win',
  GAME_OVER: 'sfx_game_over',
  TURN_START: 'sfx_turn_start',
  // BGM
  BGM_MENU: 'bgm_menu',
  BGM_GAME: 'bgm_game',
} as const;

export type SoundKey = (typeof SoundKeys)[keyof typeof SoundKeys];

// サウンドファイルの定義
const SOUND_FILES: Record<SoundKey, string> = {
  [SoundKeys.CARD_PLAY]: '/assets/sounds/sfx/card_play.mp3',
  [SoundKeys.CARD_DRAW]: '/assets/sounds/sfx/card_draw.mp3',
  [SoundKeys.CARD_DEAL]: '/assets/sounds/sfx/card_deal.mp3',
  [SoundKeys.SKIP]: '/assets/sounds/sfx/skip.mp3',
  [SoundKeys.REVERSE]: '/assets/sounds/sfx/reverse.mp3',
  [SoundKeys.UNO_CALL]: '/assets/sounds/sfx/uno_call.mp3',
  [SoundKeys.ROUND_WIN]: '/assets/sounds/sfx/round_win.mp3',
  [SoundKeys.GAME_OVER]: '/assets/sounds/sfx/game_over.mp3',
  [SoundKeys.TURN_START]: '/assets/sounds/sfx/turn_start.mp3',
  [SoundKeys.BGM_MENU]: '/assets/sounds/bgm/menu.mp3',
  [SoundKeys.BGM_GAME]: '/assets/sounds/bgm/game.mp3',
};

export class SoundManager {
  private scene: Phaser.Scene;
  private loadedSounds: Set<SoundKey> = new Set();
  private currentBGM: Phaser.Sound.BaseSound | null = null;
  private currentBGMKey: SoundKey | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    // issue #56: PreloadScene でロード済みのサウンドキーを検出して loadedSounds に登録
    // (本クラスは MainGameScene 内で生成されるため、PreloadScene の filecomplete
    //  イベントは既に発火し終わっており、自前の preloadSounds() は呼ばれない)
    Object.keys(SOUND_FILES).forEach((key) => {
      if (scene.cache.audio?.has?.(key)) {
        this.loadedSounds.add(key as SoundKey);
      }
    });
  }

  /**
   * サウンドアセットをプリロード
   * PreloadSceneから呼び出す
   */
  preloadSounds(): void {
    Object.entries(SOUND_FILES).forEach(([key, path]) => {
      // ファイルが存在しない場合のエラーを無視するため、
      // ロード完了イベントで追跡
      this.scene.load.audio(key, path);
    });

    // ロード完了時にサウンドを追跡
    this.scene.load.on('filecomplete', (key: string) => {
      if (Object.values(SoundKeys).includes(key as SoundKey)) {
        this.loadedSounds.add(key as SoundKey);
      }
    });

    // ロード失敗時は無視（サウンドファイルがない場合）
    this.scene.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(`Sound file not found: ${file.key} - Sound will be disabled for this effect`);
    });
  }

  /**
   * 効果音を再生
   */
  private playSFX(key: SoundKey, baseVolume: number = 0.8): void {
    // サウンドがロードされていない場合はスキップ
    if (!this.loadedSounds.has(key)) {
      return;
    }

    const settings = useSettingsStore.getState();

    // 効果音が無効の場合はスキップ
    if (!settings.audio.soundEnabled) {
      return;
    }

    // ボリューム計算
    const masterVol = settings.audio.masterVolume / 100;
    const sfxVol = settings.audio.soundEffectsVolume / 100;
    const finalVolume = baseVolume * masterVol * sfxVol;

    // ボリュームが0の場合はスキップ
    if (finalVolume <= 0) {
      return;
    }

    try {
      this.scene.sound.play(key, { volume: finalVolume });
    } catch (error) {
      console.warn(`Failed to play sound: ${key}`, error);
    }
  }

  /**
   * BGMを再生
   */
  playBGM(key: SoundKey): void {
    // サウンドがロードされていない場合はスキップ
    if (!this.loadedSounds.has(key)) {
      return;
    }

    const settings = useSettingsStore.getState();

    // BGMが無効の場合はスキップ
    if (!settings.audio.musicEnabled) {
      return;
    }

    // 同じBGMが再生中の場合はスキップ
    if (this.currentBGMKey === key && this.currentBGM?.isPlaying) {
      return;
    }

    // 現在のBGMを停止
    this.stopBGM();

    // ボリューム計算
    const masterVol = settings.audio.masterVolume / 100;
    const musicVol = settings.audio.musicVolume / 100;
    const finalVolume = masterVol * musicVol;

    // ボリュームが0の場合はスキップ
    if (finalVolume <= 0) {
      return;
    }

    try {
      this.currentBGM = this.scene.sound.add(key, {
        volume: finalVolume,
        loop: true,
      });
      this.currentBGM.play();
      this.currentBGMKey = key;
      console.log(`BGM started: ${key} (volume=${finalVolume.toFixed(2)}, loop=true)`);
    } catch (error) {
      console.warn(`Failed to play BGM: ${key}`, error);
    }
  }

  /**
   * BGMを停止
   */
  stopBGM(): void {
    if (this.currentBGM) {
      this.currentBGM.stop();
      this.currentBGM.destroy();
      this.currentBGM = null;
      this.currentBGMKey = null;
    }
  }

  /**
   * 設定変更時にボリュームを更新
   */
  updateVolumes(): void {
    const settings = useSettingsStore.getState();
    const masterVol = settings.audio.masterVolume / 100;

    // マスターボリュームを設定
    this.scene.sound.volume = masterVol;

    // BGMのボリュームを更新
    if (this.currentBGM && this.currentBGM.isPlaying) {
      const musicVol = settings.audio.musicVolume / 100;
      (this.currentBGM as Phaser.Sound.WebAudioSound).setVolume(masterVol * musicVol);
    }
  }

  /**
   * ミュート状態を設定
   */
  setMuted(muted: boolean): void {
    this.scene.sound.mute = muted;
  }

  // ===== 効果音再生メソッド =====

  /** カードプレイ音 */
  playCardPlay(): void {
    this.playSFX(SoundKeys.CARD_PLAY, 0.8);
  }

  /** カードドロー音 */
  playCardDraw(): void {
    this.playSFX(SoundKeys.CARD_DRAW, 0.6);
  }

  /** カード配布音 */
  playCardDeal(): void {
    this.playSFX(SoundKeys.CARD_DEAL, 0.5);
  }

  /** スキップ効果音 */
  playSkip(): void {
    this.playSFX(SoundKeys.SKIP, 0.7);
  }

  /** リバース効果音 */
  playReverse(): void {
    this.playSFX(SoundKeys.REVERSE, 0.7);
  }

  /** UNO宣言音 */
  playUnoCall(): void {
    this.playSFX(SoundKeys.UNO_CALL, 1.0);
  }

  /** ラウンド勝利音 */
  playRoundWin(): void {
    this.playSFX(SoundKeys.ROUND_WIN, 1.0);
  }

  /** ゲームオーバー音 */
  playGameOver(): void {
    this.playSFX(SoundKeys.GAME_OVER, 0.8);
  }

  /** ターン開始音 */
  playTurnStart(): void {
    this.playSFX(SoundKeys.TURN_START, 0.5);
  }

  // ===== BGM再生メソッド =====

  /** メニューBGM */
  playMenuBGM(): void {
    this.playBGM(SoundKeys.BGM_MENU);
  }

  /** ゲーム中BGM */
  playGameBGM(): void {
    this.playBGM(SoundKeys.BGM_GAME);
  }

  /**
   * サウンドがロードされているか確認
   */
  isSoundLoaded(key: SoundKey): boolean {
    return this.loadedSounds.has(key);
  }

  /**
   * ロードされているサウンドの数を取得
   */
  getLoadedSoundCount(): number {
    return this.loadedSounds.size;
  }

  /**
   * クリーンアップ
   */
  destroy(): void {
    this.stopBGM();
    this.loadedSounds.clear();
  }
}

// グローバルインスタンス（MainGameSceneから設定）
let globalSoundManager: SoundManager | null = null;

export function setSoundManager(manager: SoundManager | null): void {
  globalSoundManager = manager;
}

export function getSoundManager(): SoundManager | null {
  return globalSoundManager;
}
